import React from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import useWaitlistModal from "./useWaitlistModal";
import Privacy from "./Privacy";
import TermsOfService from "./TermsOfService";
import GoogleAPIDisclosure from "./GoogleAPIDisclosure";
import MicrosoftAPIDisclosure from "./MicrosoftAPIDisclosure";
import ScrollToTop from "./ScrollToTop";
import ProductPage from "./ProductPage";
import Faq from "./Faq";
import CaseStudies from "./CaseStudies";
import CaseStudyDetail from "./CaseStudyDetail";
import Header from "./Header";
import Footer from "./Footer";
import WaitlistModal from "./WaitlistModal";
import HomePage from "./assets/HomePage.png";
import HomePage1 from "./assets/HomePage1.png";
import salesforcelogo from "./assets/salesforcelogo.png";
import hubspotlogo from "./assets/hubspotlogo.png";
import zohologo from "./assets/zohologo.png";
import outlooklogo from "./assets/outlooklogo.png";
import calendarlogo from "./assets/google-calendar-logo.png";
import gmaillogo from "./assets/gmaillogo.png";
import documentlogo from "./assets/documentlogo.png";
import transcriptlogo from "./assets/transcriptionlogo.png";
import logo from "./assets/logo.png";
import zoomlogo from "./assets/zoomlogo.png";
import meetlogo from "./assets/meetlogo.png";
import teamslogo from "./assets/teamslogo.png";
import ciscologo from "./assets/ciscologo.png";

// Main App Component
const App = () => {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<JindoLanding />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route
            path="/google-api-disclosure"
            element={<GoogleAPIDisclosure />}
          />
          <Route
            path="/microsoft-api-disclosure"
            element={<MicrosoftAPIDisclosure />}
          />
        </Routes>
      </div>
    </Router>
  );
};

// Jindo Landing Page Component
const JindoLanding = () => {
  const {
    showWaitlistModal,
    formData,
    isSubmitting,
    isSubmitted,
    formError,
    openWaitlistModal,
    closeWaitlistModal,
    handleInputChange,
    handleSubmit,
  } = useWaitlistModal();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header onOpenWaitlist={openWaitlistModal} />

      {/* Main Content */}
      <main id="top" className="bg-gray-100">
        {/* Hero Section */}
        <section className="min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] bg-gray-100 text-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-2 md:py-16">
          <div className="max-w-7xl mx-auto w-full grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-4 sm:gap-8 md:gap-10 items-center">
            <div>
              <div className="text-sm md:text-base text-center md:text-left leading-tight font-black uppercase tracking-[0.2em] sm:tracking-[0.28em] text-sky-500 md:mb-4">
                GTM Intelligence
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight font-black m-0 md:mb-4 text-center md:text-left">
                <span className="lg:whitespace-nowrap">
                  Transcription
                  <span className="hidden md:inline"> </span>
                  <br className="hidden md:block" />
                  <span className="md:hidden"> </span>
                  was solved.
                </span>{" "}
                <span className="lg:block text-jindo-orange">
                  Memory wasn't.
                </span>
              </h1>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-slate-600 mb-2 md:mb-6 lg:max-w-3xl text-center md:text-left">
                Jindo turns every interaction across an account relationship
                into lasting institutional memory across teams and over time.
              </p>
              <div className="flex md:flex-row gap-3 justify-center md:justify-start">
                <button
                  onClick={openWaitlistModal}
                  className="inline-flex items-center justify-center rounded-full border  bg-black text-white px-6 py-3 font-bold text-sm sm:text-sm md:text-base leading-none shadow-lg shadow-jindo-orange/20 hover:-translate-y-0.5 transition-transform"
                >
                  Join the waitlist
                </button>
                <Link
                  to="/product"
                  className=" inline-flex items-center justify-center rounded-full border border-slate-900/20 bg-slate-900/5 text-slate-900 px-6 py-3 font-bold text-sm sm:text-sm md:text-base leading-none hover:-translate-y-0.5 transition-transform"
                >
                  Explore Product
                </Link>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="absolute -top-[40%] -right-[20%] w-40 sm:w-64 h-40 sm:h-64 bg-jindo-orange/10 rounded-full blur-3xl pointer-events-none"></div>
              <img
                src={HomePage1}
                alt="Jindo interface showing meeting summary and Ask Jindo account context"
                className="block w-full h-auto rounded-[22px] relative z-10"
              />
            </div>
          </div>
        </section>

        {/* Positioning Section */}
        <section
          id="positioning"
          className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-5xl mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight font-black m-0 mb-4 sm:mb-5 text-slate-900">
                Everything AI notes should have been + the relationship layer
                your CRM never captured.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 sm:mt-5 m-0 max-w-4xl">
                Jindo does not replace your CRM or act like another personal
                notetaker. It creates shared account memory from customer
                context your team already generates every day.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              <Card
                title="AI notes, plus everything they miss"
                description="Goes beyond summaries by connecting customer context to the account and relationship history."
              />
              <Card
                title="CRM holds the record, if you wish"
                description="Fills the context gap with conversations, commitments, objections, account history, and team learnings."
              />
              <Card
                title="Memory that compounds, quietly"
                description="As more people, accounts, channels, and time contribute, the memory gets richer and more useful."
              />
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-5xl mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight font-black m-0 mb-4 sm:mb-5 text-slate-900">
                Your team's best deals are built on conversations.
                <br className="hidden sm:block" />
                Then the conversations disappear.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 sm:mt-5 m-0 max-w-none">
                <span className="inline-block xl:whitespace-nowrap">
                  Every meeting starts from zero. Knowledge walks out the door.
                  Pipeline runs on recollection, not evidence.
                </span>
                <br />
                <span>
                  Jindo turns disappearing conversations into institutional
                  memory your GTM team can use.
                </span>
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              <Card
                title="Every meeting starts from zero"
                description="Context lives in scattered notes, half-read summaries, and someone's memory of what happened."
              />
              <Card
                title="Knowledge walks out the door"
                description="When a rep leaves, account history, commitments, objections, and buyer context leave with them."
              />
              <Card
                title="Pipeline runs on recollection"
                description="Pipeline and campaign conversations run on recollection instead of source-backed account evidence."
              />
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-5xl mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight font-black m-0 text-slate-900">
                What changes with Jindo?
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              <Card
                title="Nothing forgotten"
                description="Account history, commitments, and relationship context stay accessible so anyone can pick up at any time."
                minHeight="min-h-[200px] sm:min-h-[230px]"
              />
              <Card
                title="No one walks in cold"
                description="Each role gets the context it needs before the meeting, follow-up, campaign, or account review."
                minHeight="min-h-[200px] sm:min-h-[230px]"
              />
              <Card
                title="Sees what no one person can"
                description="Jindo identifies patterns across accounts; pain points, buyer pushback, engagement patterns, message resonance."
                minHeight="min-h-[200px] sm:min-h-[230px]"
              />
              <Card
                title="Gets richer over time"
                description="Every new interaction adds to the memory layer and improves what the team can learn next."
                minHeight="min-h-[200px] sm:min-h-[230px]"
              />
            </div>
            <div className="mt-8 sm:mt-9 mx-auto max-w-4xl rounded-2xl sm:rounded-3xl p-6 sm:p-7 text-center bg-gradient-to-br from-jindo-orange/10 to-sky-500/10 border border-jindo-orange/20 text-xl sm:text-2xl lg:text-3xl leading-tight font-black tracking-tight text-slate-900 shadow-xl shadow-slate-900/5">
              "Notes tell you what was said. Jindo tells you how to use the
              relationship."
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-100 border-t border-b border-slate-900/10">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-5xl mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight font-black m-0 mb-4 sm:mb-5 text-slate-900">
                Works where customer context already lives
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 sm:mt-5 m-0 max-w-4xl">
                Jindo is designed to sit above the tools and channels your GTM
                team already uses.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mt-8">
              <IntegrationBox
                title="Meetings"
                integrations={[
                  { name: "Zoom", logo: zoomlogo },
                  { name: "Google Meet", logo: meetlogo },
                  { name: "Teams", logo: teamslogo },
                  { name: "Cisco Webex", logo: ciscologo },
                ]}
              />
              <IntegrationBox
                title="CRM"
                integrations={[
                  { name: "Salesforce", logo: salesforcelogo },
                  { name: "HubSpot", logo: hubspotlogo },
                  { name: "Zoho", logo: zohologo },
                ]}
              />
              <IntegrationBox
                title="Email"
                integrations={[
                  { name: "Gmail", logo: gmaillogo },
                  { name: "Outlook", logo: outlooklogo },
                ]}
              />
              <IntegrationBox
                title="Calendar"
                integrations={[
                  { name: "Google Calendar", logo: calendarlogo },
                  { name: "Outlook Calendar", logo: outlooklogo },
                ]}
              />
              <IntegrationBox
                title="More"
                integrations={[
                  { name: "Documents", logo: documentlogo },
                  { name: "Transcripts", logo: transcriptlogo },
                ]}
              />
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-900/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-tight tracking-tight font-black mb-4 sm:mb-6 text-slate-900">
              Ready to stop losing customer context?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-slate-600 mb-6 sm:mb-8 lg:mb-10">
              Join the waitlist and benefit from Jindo's GTM intelligence.
            </p>
            <button
              onClick={openWaitlistModal}
              className="inline-flex items-center justify-center rounded-full border bg-black text-jindo-orange-600 px-6 sm:px-8 py-3 sm:py-3.5 lg:py-4 font-black text-sm sm:text-base lg:text-lg leading-none shadow-lg shadow-jindo-orange/20 hover:-translate-y-0.5 transition-transform"
            >
              Join the waitlist
            </button>
          </div>
        </section>
      </main>

      <Footer />

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeWaitlistModal();
          }}
        >
          <div className="w-full max-w-lg bg-white border border-slate-900/10 rounded-2xl sm:rounded-3xl shadow-2xl shadow-slate-900/20 p-6 sm:p-7 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeWaitlistModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-slate-900/15 bg-slate-900/5 text-slate-900 text-xl flex items-center justify-center hover:bg-slate-900/10 transition-colors"
            >
              ×
            </button>

            {!isSubmitted ? (
              <>
                <h2 className="text-2xl sm:text-3xl tracking-tight font-black m-0 mb-2 sm:mb-2.5 text-slate-900 pr-8">
                  Join the Jindo waitlist
                </h2>
                <p className="text-slate-600 leading-relaxed mt-2 sm:mt-2.5 mb-5 sm:mb-6 text-sm sm:text-base">
                  Tell us a little about you. We'll reach out with next steps
                  and early access details.
                </p>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid gap-3 sm:gap-3.5">
                    <label className="grid gap-1.5 sm:gap-2 text-slate-900 font-bold text-xs sm:text-[13px]">
                      <span>
                        Name{" "}
                        <span className="text-jindo-orange font-black ml-0.5">
                          *
                        </span>
                      </span>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        autoComplete="name"
                        className="w-full rounded-xl sm:rounded-[14px] border border-slate-900/15 bg-gray-50 text-slate-900 py-3 sm:py-3.5 px-3 sm:px-3.5 font-normal outline-none text-sm sm:text-base focus:border-jindo-orange/75 focus:ring-4 focus:ring-jindo-orange/10 transition-shadow"
                      />
                    </label>
                    <label className="grid gap-1.5 sm:gap-2 text-slate-900 font-bold text-xs sm:text-[13px]">
                      <span>
                        Work email{" "}
                        <span className="text-jindo-orange font-black ml-0.5">
                          *
                        </span>
                      </span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        autoComplete="email"
                        className="w-full rounded-xl sm:rounded-[14px] border border-slate-900/15 bg-gray-50 text-slate-900 py-3 sm:py-3.5 px-3 sm:px-3.5 font-normal outline-none text-sm sm:text-base focus:border-jindo-orange/75 focus:ring-4 focus:ring-jindo-orange/10 transition-shadow"
                      />
                    </label>
                    <label className="grid gap-1.5 sm:gap-2 text-slate-900 font-bold text-xs sm:text-[13px]">
                      <span>Company</span>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        autoComplete="organization"
                        className="w-full rounded-xl sm:rounded-[14px] border border-slate-900/15 bg-gray-50 text-slate-900 py-3 sm:py-3.5 px-3 sm:px-3.5 font-normal outline-none text-sm sm:text-base focus:border-jindo-orange/75 focus:ring-4 focus:ring-jindo-orange/10 transition-shadow"
                      />
                    </label>
                  </div>
                  {formError && (
                    <div className="text-red-500 font-bold mt-3 text-xs sm:text-sm">
                      {formError}
                    </div>
                  )}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-5">
                    <button
                      type="button"
                      onClick={closeWaitlistModal}
                      className="inline-flex items-center justify-center rounded-full border border-slate-900/20 bg-slate-900/5 text-slate-900 px-6 py-3 sm:py-3.5 font-black text-sm sm:text-[15px] leading-none hover:-translate-y-0.5 transition-transform"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center rounded-full border border-jindo-orange/80 bg-jindo-orange text-jindo-orange-950 px-6 py-3 sm:py-3.5 font-black text-sm sm:text-[15px] leading-none shadow-lg shadow-jindo-orange/20 hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center py-4 sm:py-6">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-black m-0 mb-2 sm:mb-2.5 text-slate-900">
                  Thanks for joining the journey.
                </h2>
                <p className="text-slate-600 leading-relaxed mt-2 sm:mt-2.5 mb-5 sm:mb-6 text-sm sm:text-base">
                  Thanks for signing up! Our team will contact you soon with
                  more details and help you start your Jindo journey.
                </p>
                <button
                  onClick={closeWaitlistModal}
                  className="inline-flex items-center justify-center rounded-full border border-jindo-orange/80 bg-jindo-orange text-jindo-orange-950 px-6 py-3 sm:py-3.5 font-black text-sm sm:text-[15px] leading-none shadow-lg shadow-jindo-orange/20 hover:-translate-y-0.5 transition-transform"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable Components

const Card = ({
  title,
  description,
  minHeight = "min-h-[180px] sm:min-h-[190px]",
}) => {
  return (
    <article
      className={`bg-white text-slate-900 border border-slate-900/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 ${minHeight} shadow-xl shadow-slate-900/5`}
    >
      <h3 className="text-lg sm:text-xl lg:text-[22px] leading-tight tracking-tight font-black m-0 mb-2 sm:mb-3">
        {title}
      </h3>
      <p className="text-sm sm:text-[15px] leading-relaxed text-slate-600 mt-2 sm:mt-3 m-0">
        {description}
      </p>
    </article>
  );
};

const IntegrationBox = ({ title, integrations }) => {
  return (
    <article className="min-h-[240px] sm:min-h-[260px] border border-slate-900/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 bg-jindo-orange-50/30 shadow-xl shadow-slate-900/5">
      <h3 className="text-lg sm:text-xl lg:text-[22px] tracking-tight font-black m-0 mb-4 sm:mb-5 text-slate-900">
        {title}
      </h3>
      <div className="grid gap-3 sm:gap-3.5">
        {integrations.map((integration, index) => (
          <div
            key={index}
            className="flex items-center gap-2 sm:gap-3 rounded-xl sm:rounded-2xl bg-white border border-slate-900/10 p-2.5 sm:p-3 min-h-[60px] sm:min-h-[70px] shadow-md shadow-slate-900/5"
          >
            <img
              src={integration.logo}
              alt={`${integration.name} logo`}
              className="w-10 h-10 sm:w-11 sm:h-11 min-w-[2.5rem] sm:min-w-[2.75rem] object-contain rounded-lg sm:rounded-xl bg-white p-1 sm:p-1.5 flex-shrink-0"
            />
            <div>
              <strong className="text-sm sm:text-[15px] text-slate-900">
                {integration.name}
              </strong>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
};

export default App;
